import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLocale } from '../context/LocaleContext';
import './Login.css';

export default function Login() {
  const { login, register, isAuthenticated, bootstrapping } = useAuth();
  const { t } = useLocale();
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const frontendLobbyUrl = (process.env.REACT_APP_FRONTEND_LOBBY_URL || '').trim();
  const showLobbyLink =
    mode === 'login' &&
    frontendLobbyUrl &&
    /invalid email or password/i.test(error);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      if (mode === 'login') await login(email, password);
      else await register(email, password);
    } catch (err) {
      setError(err.message || t('login.genericErr'));
    } finally {
      setSubmitting(false);
    }
  };

  if (bootstrapping) {
    return (
      <div className="login-page login-page--dark">
        <div className="login-brand">
          <span className="login-brand__mark" aria-hidden />
          <span className="login-brand__name">Custio</span>
        </div>
        <div className="login-card login-card--dark">
          <p className="login-muted">{t('login.checking')}</p>
        </div>
      </div>
    );
  }
  if (isAuthenticated) return <Navigate to="/conversations" replace />;

  const pageTitle = mode === 'login' ? t('login.titleLogin') : t('login.titleRegister');

  return (
    <div className="login-page login-page--dark">
      <div className="login-page__inner">
        <div className="login-brand">
          <span className="login-brand__mark" aria-hidden />
          <span className="login-brand__name">Custio</span>
        </div>
        <h1 className="login-hero-title">{pageTitle}</h1>

        <div className="login-card login-card--dark">
          <form onSubmit={handleSubmit} className="login-form login-form--dark">
            <div className="login-field">
              <label className="login-field__label" htmlFor="login-email">
                {t('login.email')}
              </label>
              <input
                id="login-email"
                className="login-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                placeholder={t('login.emailPh')}
                required
              />
            </div>

            <div className="login-field">
              <div className="login-field__row">
                <label className="login-field__label" htmlFor="login-password">
                  {t('login.password')}
                </label>
                {mode === 'login' && (
                  <button
                    type="button"
                    className="login-text-link"
                    title={t('login.forgotTitle')}
                    onClick={(e) => e.preventDefault()}
                  >
                    {t('login.forgot')}
                  </button>
                )}
              </div>
              <div className="login-password-wrap">
                <input
                  id="login-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                  minLength={mode === 'register' ? 8 : undefined}
                  required
                  className="login-input login-password-input"
                  placeholder={t('login.passwordPh')}
                />
                <button
                  type="button"
                  className="login-password-toggle"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-pressed={showPassword}
                  aria-label={showPassword ? t('login.hidePw') : t('login.showPw')}
                  title={showPassword ? t('login.hidePw') : t('login.showPw')}
                >
                  {showPassword ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                      <line x1="1" y1="1" x2="23" y2="23" />
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {mode === 'register' && <p className="login-hint">{t('login.hintRegister')}</p>}
            {error && (
              <p className="login-error" role="alert">
                {error}
              </p>
            )}
            {showLobbyLink && (
              <p className="login-lobby-hint">
                {t('login.lobbyHintBefore')}{' '}
                <a className="login-text-link login-text-link--inline" href={frontendLobbyUrl}>
                  {t('login.lobbyHintLink')}
                </a>
              </p>
            )}
            <button type="submit" className="login-submit login-submit--primary" disabled={submitting}>
              {submitting
                ? t('login.wait')
                : mode === 'login'
                  ? t('login.loginBtn')
                  : t('login.createAccountBtn')}
            </button>
          </form>

          <p className="login-switch">
            {mode === 'login' ? (
              <>
                <span className="login-muted">{t('login.newTo')}</span>{' '}
                <button
                  type="button"
                  className="login-text-link login-text-link--standalone"
                  onClick={() => {
                    setMode('register');
                    setError('');
                  }}
                >
                  {t('login.createAccountLink')}
                </button>
              </>
            ) : (
              <>
                <span className="login-muted">{t('login.haveAccount')}</span>{' '}
                <button
                  type="button"
                  className="login-text-link login-text-link--standalone"
                  onClick={() => {
                    setMode('login');
                    setError('');
                  }}
                >
                  {t('login.loginLink')}
                </button>
              </>
            )}
          </p>
        </div>
      </div>
    </div>
  );
}
